import { headPropsMapper } from 'data-source/mappers/headPropsMapper'
import { imageMapper } from 'data-source/mappers/imageMapper'
import { metaMapper } from 'data-source/mappers/metaMapper'
import { TeamMemberMapperOpts } from 'data-source/mappers/teamMemberMapper'
import { ApiAboutPageNewProps } from 'data-source/types.wp'
import { DSAboutNewPage, LinkedLinkProps } from 'shared-definitions/types'
import { pLinkMapper } from './pLinkMapper'
import { nullableString } from 'shared-code/conversion'

interface AboutNewPageMapperOpts extends TeamMemberMapperOpts {
  pagePath: string
  tagLinkBuilder: (slug: string) => LinkedLinkProps
}

export function aboutNewPageMapper(
  input: ApiAboutPageNewProps,
  opts: AboutNewPageMapperOpts
): DSAboutNewPage {
  return {
    resource: 'about-new-page',
    head: headPropsMapper(input.head),
    sections: null,
    ampPagePath: null,
    meta: metaMapper({ ...input, content: {} }, ['About us'], opts),
    pagePath: opts.pagePath,
    hero: {
      image: imageMapper(input.content.image, opts),
      title: input.content.about_us_label,
      heading: input.content.about_us_text,
    },
    mission: {
      title: input.content.our_mission_title,
      textHtml: input.content.our_mission_text,
      image: imageMapper(input.content.our_mission_image, opts),
    },
    ourLab: {
      number: {
        title: input.content.our_lab_stat.value,
        subTitle: input.content.our_lab_stat.text,
        icon: input.content.our_lab_stat.image,
      },
      title: input.content.our_lab_title,
      textHtml: input.content.our_lab_text,
      imageFirst: imageMapper(input.content.our_lab_image_1, opts),
      imageSecond: imageMapper(input.content.our_lab_image_2, opts),
    },
    audience: {
      title: input.content.our_audience_title,
      numbers: input.content.our_audience_stat.map(stat => ({
        title: stat.value,
        subTitle: stat.text,
        icon: stat.image,
      })),
    },
    ourTeam: {
      title: input.content.our_team_label,
      heading: input.content.our_team_title,
      members: input.content.team_members.map(member => ({
        name: member.name,
        image: imageMapper(member.image, opts),
        position: member.position,
        place: nullableString(member.location),
        joined: nullableString(member.joined_date),
        description: member.description.join(''),
        button: {
          label: 'My start page', // todo
          pLink: opts.authorLinkBuilder(member.slug),
        },
      })),
    },
    joining: {
      title: input.content.join_us_title,
      button: {
        label: input.content.join_us_button_text,
        pLink: pLinkMapper(input.content.join_us_button_link, opts),
      },
      images: input.content.join_us_images.flatMap(image => {
        const result = imageMapper(image, opts)
        if (!result) {
          return []
        }

        return result
      }),
    },
    legalBlurb: input.legal_blurb
      ? {
          pLink: input.legal_blurb.link ? pLinkMapper(input.legal_blurb.link, opts) : null,
          image: {
            light: imageMapper(input.legal_blurb.image, opts),
            dark: imageMapper(input.legal_blurb.image_dark, opts),
          },
          textHtml: input.legal_blurb.text,
        }
      : null,
    connectWith: {
      title: input.content.connect_with_sg_title,
      cards: input.content.contacts.map(contact => ({
        title: contact.title,
        text: contact.text,
        button: {
          label: contact.value,
          pLink: { href: `mailto:${contact.value}` },
        },
      })),
      socials: {
        title: input.content.follow_us_title,
        links: input.content.socials.map(social => ({
          icon: social.image,
          title: social.text,
          pLink: pLinkMapper(social.value, opts),
        })),
      },
    },
    faq: {
      title: input.content.faqs_title,
      items: input.content.faqs.map(faq => ({
        question: faq.question,
        answer: faq.answer,
      })),
    },
  }
}
